import { useState, type ReactNode } from 'react';
import { Info } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

interface InfoTooltipProps {
  /** The explanation. Keep it to a sentence or two. */
  children: ReactNode;
  /** Names what is being explained, for screen readers, e.g. "Product type". */
  label?: string;
  className?: string;
}

/**
 * Small "i" icon that holds explanatory text, so it does not clutter the page.
 *
 * Opens on hover, or on click / Enter / Space (which also covers touch). It deliberately does not
 * open on focus alone: a dialog moves focus to its first control when it opens, and when that
 * control is this icon the explanation would pop up every time, uninvited.
 */
export function InfoTooltip({ children, label, className }: InfoTooltipProps) {
  const [open, setOpen] = useState(false);
  return (
    <Tooltip open={open} onOpenChange={setOpen}>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={label ? `About ${label}` : 'More information'}
          aria-expanded={open}
          // preventDefault stops the tooltip's own handler from running after ours.
          onFocus={(e) => e.preventDefault()}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setOpen((current) => !current);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape' && open) {
              // Close the explanation without also closing the dialog it sits in.
              e.stopPropagation();
              setOpen(false);
            }
          }}
          className={cn(
            'inline-flex shrink-0 rounded-full text-muted-foreground outline-none hover:text-foreground focus-visible:ring-[3px] focus-visible:ring-ring/50',
            className
          )}
        >
          <Info className="size-3.5" aria-hidden />
        </button>
      </TooltipTrigger>
      <TooltipContent className="max-w-64 text-balance font-normal">
        {children}
      </TooltipContent>
    </Tooltip>
  );
}
